
import axios from "@/plugins/http";
import MarkdownIt from "markdown-it";
import { computed, defineComponent, nextTick, onMounted, ref, watch } from "vue-demi";

export default defineComponent({
  name: "ChatBot",

  setup(_, { emit }) {
    const loading = ref(false);
    const chatRepliesContainer = ref<HTMLElement | null>(null);
    const textareaRef = ref<HTMLElement | null>(null);

    const md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
    });

    function renderMarkdown(text: string): string {
      if (!text) return "";
      return md.render(text);
    }

    function closeChat() {
      emit("close");
    }

    const msg = ref("");

    const errorMsg = ref("");

    const ERROR_TEMPLATE = "Oops! An error occurred.";

    interface IChat {
      req: string;
      res: string;
    }

    const chatArray = ref<IChat[]>([]);

    const isEmpty = computed(() => !chatArray.value.length);

    watch(msg, () => {
      if (errorMsg.value) {
        errorMsg.value = "";
      }
    });

    onMounted(async () => {
      await nextTick();

      const chatElement = document.querySelector(".chat");
      if (chatElement) {
        const textareaElement = chatElement.querySelector("textarea");
        if (textareaElement && textareaElement.focus) {
          textareaElement.focus();
        }
      }
    });

    const DEV = "development";
    const STAGING = "staging";
    const PROD = "production";

    const DEV_URL = "https://api-dev-complience.nationbetter.uk";
    const STAGING_URL = "https://api-staging-compliance.nationbetter.uk";
    const PROD_URL = "https://api-compliance.nationbetter.uk";

    const URLS = {
      [DEV]: DEV_URL,
      [STAGING]: STAGING_URL,
      [PROD]: PROD_URL,
    };

    async function askChat() {
      loading.value = true;

      errorMsg.value = "";

      try {
        const messageHistory = chatArray.value.flatMap((item) => [
          { user: item.req },
          { chatbot: item.res },
        ]);

        const requestData = {
          Message: msg.value,
          "Message history": messageHistory,
        };

        const basePath = URLS[process.env.NODE_ENV as string] ?? "";

        if (!basePath) {
          errorMsg.value = ERROR_TEMPLATE;

          return;
        }

        const { data } = await axios.post(`${basePath}/api/gigagent/chat`, {
          user_input: JSON.stringify(requestData),
        });

        const res = data?.response || "";

        if (!res) {
          errorMsg.value = ERROR_TEMPLATE;

          return;
        }

        chatArray.value.push({
          req: msg.value,
          res,
        });

        await nextTick();

        if (chatRepliesContainer.value) {
          const replyElements = chatRepliesContainer.value.querySelectorAll(".chat-body__reply");
          const lastReplyElement = replyElements[replyElements.length - 1];
          if (lastReplyElement) {
            lastReplyElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }

        msg.value = "";
      } catch (error) {
        console.log(error);

        errorMsg.value = ERROR_TEMPLATE;
      } finally {
        loading.value = false;
      }
    }

    function clearChat() {
      chatArray.value = [];
    }

    return {
      clearChat,
      loading,
      closeChat,
      msg,
      askChat,
      isEmpty,
      chatArray,
      chatRepliesContainer,
      errorMsg,
      textareaRef,
      renderMarkdown,
    };
  },
});
